import { mdiPaperclip } from '@mdi/js';
import { Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { AttachmentButton } from '..';
import { isSupport, TooltipTransitionComponent } from '../../base';
import * as S from './compose-textarea.styles';

export interface IComposeTextareaProps {
    disabled?: boolean;
    isNote?: boolean;
    body?: string;
    onChange: (body: string, attachments: File[]) => void;
    onSave: (body: string, attachments: File[]) => void;
    ToolbarComponent?: React.ReactElement;
    textareaRef?: React.RefObject<HTMLTextAreaElement>;
}
export const ComposeTextarea: React.FunctionComponent<IComposeTextareaProps> = (
    props: IComposeTextareaProps,
): React.ReactElement => {
    const { disabled, isNote, onChange, onSave, textareaRef, ToolbarComponent } = props;

    const [body, setBody] = useState<string>(props.body || '');
    const [attachments, setAttachments] = useState<File[]>([]);
    const attachInput = useRef<HTMLInputElement | null>(null);

    // Update internal state when props.body changes (e.g., from template selection)
    useEffect(() => {
        if (props.body !== undefined && props.body !== body) {
            setBody(props.body);
        }
    }, [props.body]);

    const attachFile = (): void => {
        if (!!attachInput && attachInput.current) {
            attachInput.current.click && attachInput.current.click();
        }
    };
    const removeAttachment = (attachment: File): (() => void) => (): void =>
        setAttachments(_.without(attachments, attachment));
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        const newBody = e.target.value;
        setBody(newBody);
        onChange(newBody, attachments);
    };
    const handleAttachments = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const files = _.map(e.target.files, file => file);
        const newAttachments = _.concat(attachments, files);
        setAttachments(newAttachments);
        onChange(body, newAttachments);
    };
    const handleKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter' && e.ctrlKey) {
            onSave(body, attachments);
        }
    };
    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
        const images = _.map(e.clipboardData.items, item => {
            if (item.type.startsWith('image/')) {
                return item.getAsFile();
            }
        });
        if (images && images.length) {
            const newAttachments = _.compact(_.concat(attachments, images));
            setAttachments(newAttachments);
            onChange(body, newAttachments);
        }
    };
    const attached = _.map(
        attachments,
        (attachment: File, i: number): React.ReactElement => (
            <AttachmentButton
                key={`attachment-${i}`}
                filename={attachment.name}
                filesize={attachment.size}
                onRemove={removeAttachment(attachment)}
            />
        ),
    );

    return (
        <S.Wrapper disabled={disabled} isNote={isNote}>
            <S.InnerCol>
                {ToolbarComponent}
                <S.Textarea
                    ref={textareaRef}
                    rows={12}
                    disabled={disabled}
                    value={body}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder="Type your message here"
                />
                {attached && attached.length ? <S.InputAttachments>{attached}</S.InputAttachments> : null}
            </S.InnerCol>
            <S.AttachInput
                ref={attachInput}
                type="file"
                accept={isSupport ? undefined : 'application/pdf,.pdf,.xls,.xlsx,.csv,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'}
                onChange={handleAttachments}
            />
            <Tooltip TransitionComponent={TooltipTransitionComponent} title={<span>Upload file</span>} placement="top">
                <div>
                    <S.AttachButton variant="text" onClick={attachFile} disabled={disabled}>
                        <S.AttachIcon path={mdiPaperclip} size={0.75} />
                    </S.AttachButton>
                </div>
            </Tooltip>
        </S.Wrapper>
    );
};
